/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { ISbStoryData } from '@storyblok/react/rsc'
import { useAtomValue } from 'jotai'

import { mapStoryblokToBlogPost } from '@/lib/storyblok/mappers/blog'
import { BlogPostPageStoryblok } from '@/lib/storyblok/types/generated-types'
import { getExcerpt } from '@/lib/storyblok/utils/blog-post-extractor'
import { storyAtom } from '@/store/story'

/**
 * Component used to insert a script containing JSON-LD metadata to blog posts. Pulls the Storyblok story from local
 * state and uses that to pull the respective information.
 */
const BlogPostPageSEO = () => {
  const story = useAtomValue(storyAtom) as ISbStoryData<BlogPostPageStoryblok> | null

  if (!story || story.content?.component !== 'blog-post-page') {
    return null
  }

  const post = mapStoryblokToBlogPost(story)
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL

  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/${story.lang !== 'en' ? story.lang + '/' : ''}${story.full_slug}`,
    },
    headline: post.title,
    description: post.description,
    image: post.imageUrl,
    datePublished: story.published_at || story.first_published_at || story.created_at,
    dateModified: story.updated_at || story.published_at || story.first_published_at || story.created_at,
    author: {
      '@type': 'Organization',
      name: 'MTa Learning',
    },
    publisher: {
      '@type': 'Organization',
      name: 'MTa Learning',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/images/mta-logo.svg`,
      },
    },
    articleBody: getExcerpt(story.content, Number.MAX_SAFE_INTEGER),
  }

  return (
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }}
      key='blogposting-schema'
    />
  )
}

export { BlogPostPageSEO }
